import { Link } from 'react-router';
import { ArrowRight, Check, Rocket } from 'lucide-react';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  EmptyState,
  PageHeader,
} from '@helios/ui';
import { PageSectionWorkspace } from '@/components/layout/PageSectionWorkspace';
import type { NavDestination } from '@/types';

interface ModuleScaffoldProps {
  destination: NavDestination;
}

/**
 * Reachable, spec-sourced scaffold for a module delivered in a later sprint.
 * This is never a dead route: it renders the screen's real persona, purpose and
 * planned capabilities (from the Screen & Workflow Bible), clearly labeled with
 * its owning sprint. Later sprints replace this with the live implementation.
 */
export function ModuleScaffold({ destination }: ModuleScaffoldProps) {
  const Icon = destination.icon;
  const sprintShort = destination.sprint.split('_')[0];
  const capabilities = destination.capabilities ?? [];

  return (
    <PageSectionWorkspace
      header={
        <PageHeader
          eyebrow={destination.persona ?? 'Module'}
          title={destination.label}
          description={destination.purpose ?? destination.description}
          actions={
            <Badge variant="outline" className="gap-1.5 py-1">
              <Rocket className="text-primary h-3.5 w-3.5" aria-hidden="true" />
              {destination.sprint}
            </Badge>
          }
        />
      }
      sections={[
        {
          id: 'capabilities',
          label: 'Planned capabilities',
          content: (
            <Card className="flex min-h-0 flex-col overflow-hidden lg:col-span-2">
              <CardHeader className="flex-row items-center gap-3 space-y-0">
                <span className="bg-primary/15 text-primary flex h-10 w-10 items-center justify-center rounded-full">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <CardTitle className="text-base">Planned capabilities</CardTitle>
                  <CardDescription>Delivered in {destination.sprint}.</CardDescription>
                </div>
              </CardHeader>
              <CardContent className="min-h-0 flex-1 overflow-auto">
                {capabilities.length > 0 ? (
                  <ul className="grid gap-2 sm:grid-cols-2">
                    {capabilities.map((capability) => (
                      <li
                        key={capability}
                        className="border-border bg-background/40 text-muted-foreground flex items-start gap-2 rounded-lg border px-3 py-2 text-sm"
                      >
                        <Check
                          className="text-success mt-0.5 h-4 w-4 shrink-0"
                          aria-hidden="true"
                        />
                        <span>{capability}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    Detailed capabilities are defined in the sprint specification.
                  </p>
                )}
              </CardContent>
            </Card>
          ),
        },
        {
          id: 'delivery',
          label: 'Delivery scope',
          content: (
            <div className="flex min-h-0 flex-col overflow-auto">
              <EmptyState
                icon={<Icon className="h-5 w-5" aria-hidden="true" />}
                title={`Ships in ${sprintShort}`}
                description={`${destination.label} is scaffolded in the shell today — navigation, layout, and the design system are production-ready. The module's data and workflows are delivered in ${destination.sprint}.`}
                action={
                  <Link
                    to="/"
                    className="text-primary inline-flex items-center gap-1.5 text-xs font-medium hover:underline"
                  >
                    Back to Executive Overview
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </Link>
                }
              />
            </div>
          ),
        },
      ]}
    />
  );
}
